import React, { useState, useEffect } from "react";
import api, { HR_BASE_URL } from "../api";
import styled from "styled-components";
import { ClipboardList, Calendar, AlignLeft, Info } from "lucide-react";

const Page = styled.div`
  min-height: 100vh;
  padding: clamp(20px, 4vw, 40px);
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 800;
  background: linear-gradient(135deg, var(--primary), var(--accent));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Subtitle = styled.p`
  margin: 8px 0 0 0;
  color: var(--muted);
  font-size: 14px;
`;

const LeavesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 24px;
`;

const LeaveCard = styled.div`
  background: var(--glass);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 24px;
  box-shadow: var(--shadow);
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LeaveType = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusBadge = styled.span`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  background: ${(props) => {
    switch(props.status) {
      case "Approved": return "rgba(16, 185, 129, 0.15)";
      case "Rejected": return "rgba(239, 68, 68, 0.15)";
      default: return "rgba(245, 158, 11, 0.15)";
    }
  }};
  color: ${(props) => {
    switch(props.status) {
      case "Approved": return "#10b981";
      case "Rejected": return "#ef4444";
      default: return "#f59e0b";
    }
  }};
`;

const DateRange = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text);
  font-size: 14px;
  font-weight: 500;
`;

const ReasonText = styled.p`
  margin: 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.5;
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const AppliedOn = styled.div`
  margin-top: auto;
  font-size: 12px;
  color: var(--muted);
  border-top: 1px solid var(--border);
  padding-top: 12px;
`;

function MyLeaves() {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLeaves = async () => {
    try {
      const empId = localStorage.getItem("employee_id");
      if (!empId) return;
      const response = await api.get(`${HR_BASE_URL}leaves/my-leaves/?employee_id=${empId}`);
      setLeaves(response.data);
    } catch (err) {
      console.error("Error fetching leaves", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, []);

  return (
    <Page>
      <Container>
        <Header>
          <Title>
            <ClipboardList size={32} color="var(--primary)" />
            My Leaves
          </Title>
          <Subtitle>View your leave application history and status</Subtitle>
        </Header>

        {loading ? (
          <p>Loading your leaves...</p>
        ) : leaves.length > 0 ? (
          <LeavesGrid>
            {leaves.map((leave) => (
              <LeaveCard key={leave.id}>
                <TopRow>
                  <LeaveType>
                    <Info size={18} color="var(--accent)" />
                    {leave.leave_type}
                  </LeaveType>
                  <StatusBadge status={leave.status}>{leave.status}</StatusBadge>
                </TopRow>
                
                <DateRange>
                  <Calendar size={16} color="var(--muted)" />
                  {leave.start_date} {leave.end_date !== leave.start_date ? `to ${leave.end_date}` : ""}
                </DateRange>
                
                <ReasonText>
                  <AlignLeft size={16} color="var(--muted)" style={{ marginTop: '2px', flexShrink: 0 }} />
                  {leave.reason}
                </ReasonText>
                
                <AppliedOn>
                  Req ID: {leave.id || 'N/A'}<br/>
                  Applied on: {new Date(leave.applied_on).toLocaleDateString()}
                </AppliedOn>
                {leave.status !== "Pending" && leave.reviewed_by_name && (
                  <div style={{ marginTop: 'auto', paddingTop: '16px', borderTop: '1px solid var(--border)', fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <strong style={{ color: 'var(--text)' }}>Reviewed By:</strong>
                    <span style={{ color: 'var(--muted)' }}>{leave.reviewed_by_name}</span>
                  </div>
                )}
              </LeaveCard>
            ))}
          </LeavesGrid>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', background: 'var(--glass)', borderRadius: 'var(--radius)' }}>
            <h3 style={{ margin: '0 0 8px 0' }}>No Leaves Found</h3>
            <p style={{ color: 'var(--muted)', margin: 0 }}>You haven't requested any leaves yet.</p>
          </div>
        )}
      </Container>
    </Page>
  );
}

export default MyLeaves;
